import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Phone } from "lucide-react";
import { SITE, MEDIA } from "@/lib/site";
import Seo from "@/components/Seo";

const PERSON_JSONLD = {
  "@context": "https://schema.org",
  "@type": "Person",
  "name": "Todd Friedman",
  "jobTitle": "Richmond Signage Resource",
  "url": "https://rvasigns.com/about",
  "telephone": "+1-804-539-7300",
  "email": "todd@rvasigns.com",
  "image": "https://customer-assets.emergentagent.com/job_todd-signs-richmond/artifacts/v0nymp1d_Todd%20Professional%20Headshot.png",
  "address": {
    "@type": "PostalAddress",
    "addressLocality": "Richmond",
    "addressRegion": "VA",
    "addressCountry": "US"
  },
  "alumniOf": {
    "@type": "CollegeOrUniversity",
    "name": "Virginia Commonwealth University"
  },
  "worksFor": {
    "@type": "Organization",
    "name": "RVA Signs",
    "url": "https://rvasigns.com/"
  }
};

export default function About() {
  const pageRef = useRef(null);

  useEffect(() => {
    const root = pageRef.current;
    if (!root) return;
    const els = root.querySelectorAll(".reveal");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-visible");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: "0px 0px -60px 0px" }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <div ref={pageRef} data-testid="about-page" className="bg-white">
      <Seo
        path="/about"
        title="About Todd"
        description="Todd Friedman is a Richmond native and signage advisor helping local businesses navigate permits, landlord approvals, quotes, and timelines."
        jsonLd={PERSON_JSONLD}
      />
      {/* HERO + STORY */}
      <section className="pt-20 pb-16 lg:pt-28 lg:pb-24 bg-white" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          <div className="lg:col-span-7 order-2 lg:order-1">
            <div className="flex items-center gap-3 mb-6 reveal">
              <span className="w-10 h-0.5 bg-[#F97316]" />
              <span className="text-[11px] tracking-[0.22em] uppercase font-bold text-[#F97316]">About Todd</span>
            </div>
            <h1 className="font-heading text-[#1A1A1A] tracking-tight leading-[0.98] reveal reveal-delay-1" style={{ fontWeight: 900, fontSize: "clamp(3rem, 6vw, 5.5rem)" }} data-testid="about-headline">
              About Todd.
            </h1>
            <p className="mt-7 font-heading text-2xl sm:text-3xl text-[#F97316] leading-tight reveal reveal-delay-2" style={{ fontWeight: 700 }} data-testid="about-subheadline">
              Richmond native. Advertising guy. Signage advisor.
            </p>
            {/* Mobile/tablet portrait — rounded rectangle, capped width so it stays a supporting element */}
            <div className="lg:hidden mt-10 reveal reveal-delay-3" data-testid="about-mobile-portrait">
              <img
                src={MEDIA.toddPortraitBW}
                alt="Todd Friedman"
                className="block w-full max-w-[280px] h-auto rounded-xl bg-[#F8F8F5] border border-[#1A1A1A]/8 mx-auto"
                loading="lazy"
              />
            </div>
            {/* Story / About copy — flows directly below the tagline in the same column */}
            <div className="mt-10 lg:mt-12 space-y-6 text-lg lg:text-xl text-[#2B2F33]/90 leading-[1.7]">
              <p className="reveal">I grew up in Richmond and graduated from VCU with a degree in Marketing and Advertising before moving to Las Vegas, where I spent the next twenty years working in advertising.</p>
              <p className="reveal">Most of that time was spent helping businesses reach customers through outdoor advertising, media planning, and marketing strategy.</p>
              <p className="font-heading text-[#1A1A1A] text-2xl sm:text-3xl leading-tight pt-2 reveal" style={{ fontWeight: 800 }}>In 2023, I moved back to Richmond.</p>
              <p className="reveal">Today, I work alongside the team at <strong className="text-[#1A1A1A] font-semibold">Superior Signs</strong>, helping business owners, property managers, healthcare providers, franchise operators, and contractors navigate signage projects throughout Central Virginia.</p>
              <p className="reveal">The sign industry can be confusing if you don&rsquo;t work in it every day. Permits, landlord approvals, zoning requirements, budgets, timelines, and competing opinions can make a simple project feel more complicated than it should be.</p>
              <p className="font-heading text-[#1A1A1A] text-xl sm:text-2xl leading-tight pt-3 reveal" style={{ fontWeight: 700 }}>If I can help, I will.<br />If I can&rsquo;t, I&rsquo;ll point you in the right direction.</p>
            </div>
          </div>
          <div className="hidden lg:block lg:col-span-5 order-1 lg:order-2">
            <div className="max-w-md lg:max-w-none mx-auto reveal reveal-img lg:sticky lg:top-32">
              <img
                src={MEDIA.toddPortraitBW}
                alt="Todd Friedman"
                className="block w-full h-auto bg-[#F8F8F5]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* PERSPECTIVE — removed per page revision; About copy now flows directly into closing CTA */}

      {/* CLOSING */}
      <section className="bg-white pt-12 pb-20 lg:pt-16 lg:pb-28 border-t border-[#1A1A1A]/8" data-testid="about-closing">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-heading text-[#1A1A1A] tracking-tight leading-[1.05] reveal" style={{ fontWeight: 900, fontSize: "clamp(2.25rem, 4.5vw, 4rem)" }}>
            You don&rsquo;t need to know everything about signs.
          </h2>
          <p className="mt-6 font-heading text-2xl sm:text-3xl text-[#F97316] leading-tight reveal reveal-delay-1" style={{ fontWeight: 700 }}>
            You just need someone to call.
          </p>
          <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4 reveal reveal-delay-2">
            <Link
              to="/contact"
              className="bg-[#F97316] hover:bg-[#EA580C] text-white text-sm font-bold uppercase tracking-wider px-8 py-4 inline-flex items-center gap-2 transition-colors"
              data-testid="about-cta-talk"
            >
              Talk to Todd <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href={SITE.phoneHref}
              className="text-sm font-bold text-[#1A1A1A] hover:text-[#F97316] inline-flex items-center gap-2 px-8 py-4 transition-colors"
              data-testid="about-cta-phone"
            >
              <Phone className="w-4 h-4 text-[#F97316]" /> {SITE.phone}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
